import { useState, useEffect } from 'react';
import { Home, Building2, Wallet, CreditCard, Briefcase, Plus, Info, DollarSign, ChevronLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface Category {
  id: string;
  name: string;
  type: string;
}

const ASSET_CATEGORIES = [
  { id: 'real_estate', name: 'Property', icon: Home, description: 'Real estate assets' },
  { id: 'bank_account', name: 'Savings', icon: Wallet, description: 'Cash and bank accounts' },
  { id: 'investment', name: 'Investments', icon: Briefcase, description: 'Stocks, bonds, and funds' },
];

const LIABILITY_CATEGORIES = [
  { id: 'mortgage', name: 'Mortgage', icon: Building2, description: 'Home loans' },
  { id: 'credit_card', name: 'Credit Card', icon: CreditCard, description: 'Credit card debt' },
  { id: 'loan', name: 'Loan', icon: Wallet, description: 'Personal or auto loans' },
];

const INCOME_CATEGORIES = [
  { id: 'salary', name: 'Salary', icon: DollarSign, description: 'Regular employment income' },
  { id: 'investment', name: 'Investment', icon: Briefcase, description: 'Investment returns' },
  { id: 'rental', name: 'Rental', icon: Home, description: 'Rental property income' },
  { id: 'other', name: 'Other', icon: Wallet, description: 'Other income sources' },
];

const EXPENSE_CATEGORIES = [
  { id: 'housing', name: 'Housing', icon: Home, description: 'Rent, mortgage, utilities' },
  { id: 'transportation', name: 'Transportation', icon: Wallet, description: 'Car, fuel, public transit' },
  { id: 'food', name: 'Food', icon: Wallet, description: 'Groceries and dining' },
  { id: 'other', name: 'Other', icon: Wallet, description: 'Other expenses' },
];

const PROPERTY_TYPES = [
  { id: 'main_residence', name: 'Main Residence' },
  { id: 'rental_property', name: 'Rental Property' },
];

const PAYMENT_TYPES = [
  { id: 'repayment', name: 'Repayment' },
  { id: 'interest_only', name: 'Interest Only' },
];

const FREQUENCY_OPTIONS = [
  { id: 'one-time', name: 'One Time' },
  { id: 'monthly', name: 'Monthly' },
  { id: 'quarterly', name: 'Quarterly' },
  { id: 'annual', name: 'Annual' },
];

const inputClass = 'border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-slate-900/20 focus-visible:border-slate-400 rounded-lg h-10';
const labelClass = 'text-slate-900 font-medium';

interface AddItemModalProps {
  onClose: () => void;
  onSuccess: () => void;
  initialItemType?: 'asset' | 'liability';
  initialCategory?: string;
}

export default function AddItemModal({ onClose, onSuccess, initialItemType, initialCategory }: AddItemModalProps) {
  const { user, profile } = useAuth();
  const [itemType, setItemType] = useState<'asset' | 'liability' | 'income' | 'expense' | null>(initialItemType ?? null);
  const [selectedCategory, setSelectedCategory] = useState<string>(initialCategory ?? '');
  const [categories, setCategories] = useState<Category[]>([]);
  const [propertyType, setPropertyType] = useState<string>('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    value: '',
    purchaseValue: '',
    growthRate: '3',
    description: '',
    interestRate: '',
    paymentType: 'repayment',
    paymentAmount: '',
    mortgageStartDate: new Date().toISOString().split('T')[0],
    frequency: 'monthly',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    isVariable: false,
    dueDate: '',
    includeMortgage: false,
    mortgageAmount: '',
  });

  useEffect(() => {
    if (itemType === 'income' || itemType === 'expense') {
      fetchCategories();
    }
  }, [itemType]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('type', itemType);

      if (error) throw error;
      setCategories(data || []);
    } catch {
      setError('Failed to load categories');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!user) {
        setError('No user found. Please log in again.');
        return;
      }
      if (!validateForm()) return;

      setIsSubmitting(true);
      setError('');

      const value = parseFloat(formData.value);

      if (itemType === 'income' || itemType === 'expense') {
        const category = categories.find(c => c.name.toLowerCase() === selectedCategory.toLowerCase());
        if (!category) throw new Error('Invalid category selected');

        const { error: insertError } = await supabase.from('cash_flows').insert({
          user_id: user.id,
          name: formData.name.trim(),
          type: itemType,
          amount: value,
          frequency: formData.frequency,
          start_date: formData.startDate,
          end_date: formData.endDate || null,
          category_id: category.id,
          is_variable: formData.isVariable,
          due_date: formData.dueDate ? parseInt(formData.dueDate) : null,
          description: formData.description?.trim() || null,
        });
        if (insertError) throw insertError;
      } else {
        const baseData = {
          user_id: user.id,
          name: formData.name.trim(),
          type: selectedCategory,
          description: formData.description?.trim() || null,
          currency: profile?.currency || 'USD',
        };

        if (itemType === 'asset') {
          const { error: insertError } = await supabase.from('assets').insert({
            ...baseData,
            value,
            subtype: propertyType || null,
            purchase_value: formData.purchaseValue ? parseFloat(formData.purchaseValue) : null,
            growth_rate: formData.growthRate ? parseFloat(formData.growthRate) : 3,
          });
          if (insertError) throw insertError;

          if (selectedCategory === 'real_estate' && formData.includeMortgage) {
            const mortgageAmount = parseFloat(formData.mortgageAmount);
            if (!isNaN(mortgageAmount) && mortgageAmount > 0) {
              const { error: liabilityError } = await supabase.from('liabilities').insert({
                user_id: user.id,
                name: `Mortgage – ${formData.name.trim()}`,
                type: 'mortgage',
                amount: mortgageAmount,
                currency: profile?.currency || 'USD',
                description: null,
                interest_rate: formData.interestRate ? parseFloat(formData.interestRate) : null,
                payment_type: formData.paymentType || null,
                payment_amount: formData.paymentAmount ? parseFloat(formData.paymentAmount) : null,
                start_date: formData.mortgageStartDate || null,
              });
              if (liabilityError) throw liabilityError;
            }
          }
        } else {
          const { error: insertError } = await supabase.from('liabilities').insert({
            ...baseData,
            amount: value,
            interest_rate: formData.interestRate ? parseFloat(formData.interestRate) : null,
            payment_type: selectedCategory === 'mortgage' ? formData.paymentType : null,
            payment_amount: selectedCategory === 'mortgage' && formData.paymentAmount ? parseFloat(formData.paymentAmount) : null,
            start_date: selectedCategory === 'mortgage' ? formData.mortgageStartDate : null,
          });
          if (insertError) throw insertError;
        }
      }
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add item. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    const value = parseFloat(formData.value);
    if (isNaN(value) || value < 0) {
      setError('Please enter a valid value');
      return false;
    }
    if (selectedCategory === 'real_estate') {
      const purchaseValue = parseFloat(formData.purchaseValue);
      if (isNaN(purchaseValue) || purchaseValue < 0) {
        setError('Please enter a valid purchase value');
        return false;
      }
      if (formData.includeMortgage) {
        const mortgageAmount = parseFloat(formData.mortgageAmount);
        if (isNaN(mortgageAmount) || mortgageAmount <= 0) {
          setError('Please enter a valid mortgage balance');
          return false;
        }
        const paymentAmount = parseFloat(formData.paymentAmount);
        if (isNaN(paymentAmount) || paymentAmount <= 0) {
          setError('Please enter a valid monthly payment');
          return false;
        }
        if (!formData.mortgageStartDate) {
          setError('Please enter a mortgage start date');
          return false;
        }
      }
    }
    if (selectedCategory === 'mortgage') {
      const paymentAmount = parseFloat(formData.paymentAmount);
      if (isNaN(paymentAmount) || paymentAmount <= 0) {
        setError('Please enter a valid payment amount');
        return false;
      }
      if (!formData.mortgageStartDate) {
        setError('Please enter a start date');
        return false;
      }
    }
    if (itemType === 'liability' && formData.interestRate) {
      const interestRate = parseFloat(formData.interestRate);
      if (isNaN(interestRate) || interestRate < 0) {
        setError('Please enter a valid interest rate');
        return false;
      }
    }
    if (itemType === 'income' || itemType === 'expense') {
      if (!formData.startDate || !formData.frequency) {
        setError('Please enter a start date and select a frequency');
        return false;
      }
    }
    return true;
  };

  const step = !itemType ? 1 : !selectedCategory ? 2 : 3;

  const renderStepIndicator = () => (
    <div className="flex justify-center mb-6">
      <div className="flex items-center">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center">
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                s === step ? 'bg-slate-900 text-white' : s < step ? 'bg-slate-200 text-slate-700' : 'bg-slate-100 text-slate-400'
              }`}
            >
              {s}
            </div>
            {s < 3 && <div className="mx-1 h-px w-6 bg-slate-200" />}
          </div>
        ))}
      </div>
    </div>
  );

  const renderTypeStep = () => (
    <div className="grid grid-cols-2 gap-3">
      {[
        { type: 'asset' as const, label: 'Asset', desc: 'Something you own', icon: Wallet, color: 'bg-slate-100 text-slate-700' },
        { type: 'liability' as const, label: 'Liability', desc: 'Something you owe', icon: CreditCard, color: 'bg-slate-100 text-slate-700' },
        { type: 'income' as const, label: 'Income', desc: 'Source of income', icon: DollarSign, color: 'bg-slate-100 text-slate-700' },
        { type: 'expense' as const, label: 'Expense', desc: 'Recurring expense', icon: Briefcase, color: 'bg-slate-100 text-slate-700' },
      ].map(({ type, label, desc, icon: Icon, color }) => (
        <button
          key={type}
          type="button"
          onClick={() => setItemType(type)}
          className={`flex flex-col items-center gap-2 rounded-xl border-2 p-6 text-left transition-all hover:border-slate-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:ring-offset-2 ${
            itemType === type ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white'
          }`}
        >
          <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
          <span className="font-semibold text-slate-900">{label}</span>
          <span className="text-sm text-slate-600">{desc}</span>
        </button>
      ))}
    </div>
  );

  const categoriesList = itemType === 'asset' ? ASSET_CATEGORIES :
    itemType === 'liability' ? LIABILITY_CATEGORIES :
    itemType === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  const renderCategoryStep = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {categoriesList.map((category) => {
        const Icon = category.icon;
        return (
          <button
            key={category.id}
            type="button"
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center gap-4 rounded-xl border-2 p-4 text-left transition-all hover:border-slate-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:ring-offset-2 ${
              selectedCategory === category.id ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white'
            }`}
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
              <Icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-slate-900">{category.name}</div>
              <div className="text-sm text-slate-600">{category.description}</div>
            </div>
          </button>
        );
      })}
    </div>
  );

  const renderCashFlowForm = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className={labelClass}>Name</Label>
        <Input className={inputClass} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={itemType === 'income' ? 'e.g. Salary' : 'e.g. Rent'} required />
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Amount</Label>
        <Input type="number" step="0.01" min="0" className={inputClass} value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} placeholder="0.00" required />
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Frequency</Label>
        <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
          <SelectTrigger className={inputClass}>
            <SelectValue placeholder="Select frequency" />
          </SelectTrigger>
          <SelectContent>
            {FREQUENCY_OPTIONS.map((opt) => (
              <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className={labelClass}>Start date</Label>
          <Input type="date" className={inputClass} value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} required />
        </div>
        <div className="space-y-2">
          <Label className={labelClass}>End date (optional)</Label>
          <Input type="date" className={inputClass} value={formData.endDate} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} />
        </div>
      </div>
      {itemType === 'expense' && (
        <div className="space-y-2">
          <Label className={labelClass}>Due day of month (optional)</Label>
          <Input type="number" min="1" max="31" className={inputClass} value={formData.dueDate} onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })} placeholder="1–31" />
        </div>
      )}
      <div className="flex items-center gap-2">
        <Checkbox id="isVariable" checked={formData.isVariable} onCheckedChange={(checked) => setFormData({ ...formData, isVariable: checked === true })} className="border-slate-300 data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900" />
        <Label htmlFor="isVariable" className="text-sm text-slate-600 font-normal cursor-pointer">Variable amount (changes month to month)</Label>
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Notes (optional)</Label>
        <Textarea className={`${inputClass} min-h-[80px]`} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Add a note" rows={3} />
      </div>
    </div>
  );

  const renderPropertyForm = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-slate-900">Property details</h4>
        <div className="space-y-2">
          <Label className={labelClass}>Property name</Label>
          <Input className={inputClass} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Main home" required />
        </div>
        <div className="space-y-2">
          <Label className={labelClass}>Property type</Label>
          <Select value={propertyType} onValueChange={setPropertyType}>
            <SelectTrigger className={inputClass}>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className={labelClass}>Purchase value</Label>
            <Input type="number" step="0.01" min="0" className={inputClass} value={formData.purchaseValue} onChange={(e) => setFormData({ ...formData, purchaseValue: e.target.value })} placeholder="0" required />
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>Current value</Label>
            <Input type="number" step="0.01" min="0" className={inputClass} value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} placeholder="0" required />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 space-y-2">
            <Label className={labelClass}>Expected annual growth (%)</Label>
            <Input type="number" step="0.1" min="0" className={inputClass} value={formData.growthRate} onChange={(e) => setFormData({ ...formData, growthRate: e.target.value })} placeholder="3" />
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" className="mt-8 shrink-0 text-slate-500 hover:text-slate-700 hover:bg-slate-100">
                  <Info className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs border-slate-200 bg-white text-slate-700">
                Expected annual appreciation. Default 3% is based on historical average.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="space-y-2">
          <Label className={labelClass}>Notes (optional)</Label>
          <Textarea className={`${inputClass} min-h-[80px]`} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="e.g. Address or details" rows={3} />
        </div>
      </div>

      <Separator className="bg-slate-200" />

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="includeMortgage"
            checked={formData.includeMortgage}
            onCheckedChange={(checked) => setFormData({ ...formData, includeMortgage: checked === true })}
            className="border-slate-300 data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900"
          />
          <Label htmlFor="includeMortgage" className="text-sm font-semibold text-slate-900 cursor-pointer">
            I have a mortgage on this property
          </Label>
        </div>
        <p className="text-sm text-slate-600">
          Add mortgage details here to track equity (property value minus mortgage balance) in one go.
        </p>

        {formData.includeMortgage && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-4">
            <div className="space-y-2">
              <Label className={labelClass}>Outstanding balance</Label>
              <Input type="number" step="0.01" min="0" className={inputClass} value={formData.mortgageAmount} onChange={(e) => setFormData({ ...formData, mortgageAmount: e.target.value })} placeholder="Amount still owed" required={formData.includeMortgage} />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Interest rate (%)</Label>
              <Input type="number" step="0.01" min="0" className={inputClass} value={formData.interestRate} onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })} placeholder="e.g. 4.5" />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Payment type</Label>
              <Select value={formData.paymentType} onValueChange={(v) => setFormData({ ...formData, paymentType: v })}>
                <SelectTrigger className={inputClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_TYPES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>Monthly payment</Label>
                <Input type="number" step="0.01" min="0" className={inputClass} value={formData.paymentAmount} onChange={(e) => setFormData({ ...formData, paymentAmount: e.target.value })} placeholder="0" required={formData.includeMortgage} />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Start date</Label>
                <Input type="date" className={inputClass} value={formData.mortgageStartDate} onChange={(e) => setFormData({ ...formData, mortgageStartDate: e.target.value })} required={formData.includeMortgage} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderMortgageForm = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className={labelClass}>Mortgage name</Label>
        <Input className={inputClass} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Main residence mortgage" required />
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Outstanding balance</Label>
        <Input type="number" step="0.01" min="0" className={inputClass} value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} placeholder="0" required />
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Interest rate (%)</Label>
        <Input type="number" step="0.01" min="0" className={inputClass} value={formData.interestRate} onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })} placeholder="e.g. 4.5" />
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Payment type</Label>
        <Select value={formData.paymentType} onValueChange={(v) => setFormData({ ...formData, paymentType: v })}>
          <SelectTrigger className={inputClass}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_TYPES.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className={labelClass}>Monthly payment</Label>
          <Input type="number" step="0.01" min="0" className={inputClass} value={formData.paymentAmount} onChange={(e) => setFormData({ ...formData, paymentAmount: e.target.value })} placeholder="0" required />
        </div>
        <div className="space-y-2">
          <Label className={labelClass}>Start date</Label>
          <Input type="date" className={inputClass} value={formData.mortgageStartDate} onChange={(e) => setFormData({ ...formData, mortgageStartDate: e.target.value })} required />
        </div>
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>Notes (optional)</Label>
        <Textarea className={`${inputClass} min-h-[80px]`} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Lender or reference" rows={3} />
      </div>
    </div>
  );

  const renderStandardForm = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className={labelClass}>Name</Label>
        <Input className={inputClass} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Savings account" required />
      </div>
      <div className="space-y-2">
        <Label className={labelClass}>{itemType === 'asset' ? 'Value' : 'Amount owed'}</Label>
        <Input type="number" step="0.01" min="0" className={inputClass} value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} placeholder="0" required />
      </div>
      {itemType === 'liability' && (
        <div className="space-y-2">
          <Label className={labelClass}>Interest rate (%)</Label>
          <Input type="number" step="0.01" min="0" className={inputClass} value={formData.interestRate} onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })} placeholder="Optional" />
        </div>
      )}
      <div className="space-y-2">
        <Label className={labelClass}>Notes (optional)</Label>
        <Textarea className={`${inputClass} min-h-[80px]`} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Add a note" rows={3} />
      </div>
    </div>
  );

  const renderForm = () => {
    if (selectedCategory === 'real_estate') return renderPropertyForm();
    if (selectedCategory === 'mortgage') return renderMortgageForm();
    if (itemType === 'income' || itemType === 'expense') return renderCashFlowForm();
    return renderStandardForm();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-slate-200 rounded-2xl bg-white shadow-xl">
        <DialogHeader>
          <DialogTitle className="text-slate-900 text-xl">
            Add {itemType ? itemType.charAt(0).toUpperCase() + itemType.slice(1) : 'item'}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {step === 1 && 'Choose what you want to add.'}
            {step === 2 && 'Select a category.'}
            {step === 3 && 'Enter the details below.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {renderStepIndicator()}

          {error && (
            <Alert variant="destructive" className="mb-4 border-red-200 bg-red-50 [&>svg]:text-red-600">
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          {step === 1 && renderTypeStep()}
          {step === 2 && renderCategoryStep()}
          {step === 3 && (
            <div className={isSubmitting ? 'pointer-events-none opacity-60' : ''}>
              {renderForm()}
            </div>
          )}

          <Separator className="my-6 bg-slate-200" />

          <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (selectedCategory) setSelectedCategory('');
                else if (itemType) setItemType(null);
                else onClose();
              }}
              disabled={isSubmitting}
              className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {selectedCategory || itemType ? 'Back' : 'Cancel'}
            </Button>
            {step === 3 && (
              <Button
                type="submit"
                disabled={isSubmitting}
                className="rounded-full bg-slate-900 text-white hover:bg-slate-800 font-medium"
              >
                {isSubmitting ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
